import React, { useCallback, useMemo } from 'react';
import styles from './Ecommerce.module.css';
import { MuiTab } from '@ace/ace-ui-web';
import SalesByFulfillment from './salesbyfulfillment/SalesByFulfillment';
import ProductSyncDetails from './productsyncdetails/ProductSyncDetails';
import OrderDetails from './orderdetails/OrderDetails';
import KeyPerformanceIndicators from './keyperformanceindicators/KeyPerformanceIndicators';
import KeyOperationalMetrics from './keyoperationalmetrics/KeyOperationalMetrics';
import YardRx from './yardrx/YardRx';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useScrollSpy } from '../../../../hooks/useScrollSpy';
import lang from '../../../../languages/en.json';
import { useClickHandler } from '../../../../utils/usageMetricsLogger';

const PAGE_NM = 'Performance';
const TAB_NM = 'Ecommerce';

const Ecommerce: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);

  // MobileQuery Responsive
  const isResponsive = useMediaQuery('(max-width:1195px)');
  const isMobile = useMediaQuery('(max-width:600px)');

  const sections = useMemo(
    () => [
      {
        id: 'sales-by-fulfillment',
        component: <SalesByFulfillment />,
        label: lang.ecommerceContent.salesByFulfillmentTitle,
      },
      {
        id: 'key-performance-indicators',
        component: <KeyPerformanceIndicators />,
        label: lang.ecommerceContent.keyPerformanceIndicatorsTabTitle,
      },
      {
        id: 'order-details',
        component: <OrderDetails />,
        label: lang.ecommerceContent.orderDetailsTabTitle,
      },
      {
        id: 'key-operational-metrics',
        component: <KeyOperationalMetrics />,
        label: lang.ecommerceContent.keyOperationalMetricTabTitle,
      },
      {
        id: 'product-sync-details',
        component: <ProductSyncDetails />,
        label: lang.ecommerceContent.productSyncDetailsTabTitle,
      },
      { id: 'yard-rx', component: <YardRx />, label: lang.ecommerceContent.yardRxTabTitle },
    ],
    [],
  );

  // References for section navigation
  const sectionRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  // Scroll spy hook for automatic tab highlighting based on scroll position
  useScrollSpy(sectionRefs, setActiveTab, sections.length);

  const handleTabChange = useCallback(
    (index: number): void => {
      setActiveTab(index);
      handleAndLogClick(sections[index]?.label);
      const element = document.getElementById(sections[index].id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [handleAndLogClick, sections],
  );

  return (
    <div className={styles.container}>
      {/* Submenu Navigation */}
      <nav className={styles.subMenuNav}>
        <MuiTab
          fontSize={isMobile ? 'small' : 'default'}
          scrollable={isResponsive}
          // renderPanels hides the content panels. In tabs prop only label is being displayed in the UI.
          renderPanels={false}
          value={activeTab}
          onChange={handleTabChange}
          tabs={sections.map(section => ({
            content: null,
            label: section.label,
          }))}
          sx={{
            marginBottom: '20px',
          }}
        />
      </nav>

      {/* Sections */}
      {sections.map((section, index) => (
        <section
          key={section.id}
          id={section.id}
          ref={el => (sectionRefs.current[index] = el as HTMLDivElement | null)}
          className={`${styles.section} ${index === 0 ? styles.firstSection : ''}`}>
          {section.component}
        </section>
      ))}
    </div>
  );
};

export default Ecommerce;
